import React from "react";
import { Link } from "react-router-dom";
import { Heart, Sparkles as SparklesIcon, ShieldCheck } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import { useSeo } from "@/lib/useSeo";
import { useSiteContent, resolveImageUrl } from "@/lib/siteContent";
import { IMAGES } from "@/lib/data";

export default function AboutPage() {
  const { content } = useSiteContent();
  useSeo("About", "About Us");
  const site = content?.site || {};
  const paragraphs = (site.aboutPageBody || "").split(/\n\s*\n/).filter(Boolean);
  const aboutImg = site.aboutImageUrl || IMAGES.about;

  return (
    <div data-testid="about-page">
      <PageHeader title="Our Story" script="The Cozy Cove" subtitle="A family-owned dream, delivered to your door." />

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src={resolveImageUrl(aboutImg)} alt="Family" className="rounded-3xl shadow-cozy-lg w-full h-[500px] object-cover" loading="lazy" />
          </Reveal>
          <Reveal delay={150}>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight">
              We believe childhood deserves <span className="font-script text-cozy-gold">magic</span>
            </h2>
            {paragraphs.length > 0 ? paragraphs.map((p, i) => (
              <p key={i} className="text-cozy-brown-soft mt-5 leading-relaxed text-lg">{p}</p>
            )) : (
              <>
                <p className="text-cozy-brown-soft mt-5 leading-relaxed text-lg">
                  The Cozy Cove began as a kitchen-table idea — a way to give kids the kind of sleepover
                  that lives forever in their memory.
                </p>
                <p className="text-cozy-brown-soft mt-4 leading-relaxed text-lg">
                  We're a family business. We answer our own messages. We hand-pick every blanket.
                </p>
              </>
            )}
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-cozy-beige/40">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-xs tracking-[0.3em] text-cozy-gold uppercase">Our Values</span>
              <h2 className="font-serif text-4xl sm:text-5xl mt-4">What we stand for</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-7 mt-14">
            {[
              { icon: Heart, t: "Heart First", d: "Every event is treated like it's for our own children." },
              { icon: SparklesIcon, t: "Magic in the Details", d: "From the pillow placement to the breakfast tray." },
              { icon: ShieldCheck, t: "Clean & Safe", d: "Professional sanitizing standards on every item." },
            ].map(({ icon: Icon, t, d }, i) => (
              <Reveal key={t} delay={i * 100}>
                <div className="bg-white rounded-3xl p-8 shadow-cozy text-center border border-cozy-gold/10 h-full" data-testid={`value-${i}`}>
                  <div className="w-14 h-14 rounded-full bg-cozy-pink/70 mx-auto flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-cozy-gold" />
                  </div>
                  <h3 className="font-serif text-2xl">{t}</h3>
                  <p className="text-cozy-brown-soft mt-3">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 text-center">
        <div className="max-w-3xl mx-auto px-5">
          <h2 className="font-serif text-4xl sm:text-5xl">Let's create your <span className="font-script text-cozy-gold">cozy</span> night</h2>
          <Link to="/booking" data-testid="about-cta-book" className="inline-flex mt-8 px-8 py-3.5 rounded-full bg-cozy-gold text-white hover:bg-cozy-gold-dark transition-all shadow-cozy-lg">
            Book Your Party
          </Link>
        </div>
      </section>
    </div>
  );
}

export function PageHeader({ title, script, subtitle }) {
  return (
    <section className="py-20 bg-cozy-pink/30 text-center" data-testid="page-header">
      <div className="max-w-3xl mx-auto px-5">
        <span className="text-xs tracking-[0.3em] text-cozy-gold uppercase">{title}</span>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl mt-4">
          <span className="font-script text-cozy-gold">{script}</span>
        </h1>
        {subtitle && <p className="text-cozy-brown-soft mt-4 text-lg">{subtitle}</p>}
      </div>
    </section>
  );
}
